import { SmartDentConstant } from '../actions/constants/SmartDentConstant';
import { LICENSE_PRODUCTS, DEFAULT_LICENSE_PRODUCT } from '../actions/constants/LicenseProductConstant';

const initialState = {
    packages: [],
    selectedPackage: null,
    corpAllocation: null,
    dealerAllocations: [],
    dealerAllocation: null,
    // IMP-387: 중간 법인이 상위로부터 "받은 예산" 전용 슬롯. dealerAllocation(선택 자식) 오염 방지.
    myReceivedAllocation: null,
    customerLicenses: [],
    issuedQrs: [],
    // FEAT-022: split customer license slices to avoid collision
    corpCustomerLicenses: [],
    dealerCustomerLicenses: [],
    issuedQrHistory: [],
    // FEAT-023: voucher print template (SPEC-RG-05)
    voucherTemplate: null,
    // FEAT-024: allocation audit history (SPEC-RG-01 AC-3 / SPEC-RG-02 AC-7)
    // grantHistory          : HQ → Corp 부여 이력 (operation='grant')
    // dealerAllocationHistory: Corp ↔ Dealer 할당/회수 이력 (operation∈{allocate,revoke})
    grantHistory: [],
    dealerAllocationHistory: [],
    // FEAT-050 S5a: N단계 서브트리 할당 노드 맵 { [companyId]: record }
    allocationNodes: {},
    // IMP-420(#354) 문제 1: resolveScopeContext(thunk SSoT) 판정 { companyId, managerGroupId,
    // isSubEntity } 저장. smartDentQuota.resolveEffectiveCorpBudget 자체 판정과 갈리는 문제 해소.
    scopeContext: null,
};

// FEAT-058(#575): 16슬롯 초기값을 담은 불변 상수. 제품 칸이 아직 없을 때의 읽기 기본값이자 새 칸의 시작값.
export const EMPTY_LICENSE_PRODUCT_STATE = Object.freeze({ ...initialState });

// 한 제품 칸(slice)에 action 을 적용한다. 칸이 바뀌지 않으면 같은 참조를 반환한다.
const reduceProductSlice = (state, action) => {
    switch (action.type) {
        case SmartDentConstant.GET_PACKAGES:
            return { ...state, packages: action.packages };
        case SmartDentConstant.GET_PACKAGE:
            return { ...state, selectedPackage: action.selectedPackage };
        case SmartDentConstant.GET_CORP_ALLOCATION:
            return { ...state, corpAllocation: action.corpAllocation };
        case SmartDentConstant.GET_DEALER_ALLOCATIONS:
            return { ...state, dealerAllocations: action.dealerAllocations };
        case SmartDentConstant.GET_DEALER_ALLOCATION:
            return { ...state, dealerAllocation: action.dealerAllocation };
        case SmartDentConstant.GET_MY_RECEIVED_ALLOCATION:
            return { ...state, myReceivedAllocation: action.myReceivedAllocation };
        case SmartDentConstant.GET_CUSTOMER_LICENSES:
            return { ...state, customerLicenses: action.customerLicenses };
        case SmartDentConstant.UPDATE_CUSTOMER_LICENSE: {
            // source='voucher_license': MongoDB ObjectId 기준 매칭 (SuspendLicense/ResumeLicense).
            // source 없음: DDB 기반 _id+sk 복합 키 매칭 (legacy UpdateLicenseMemo 등).
            // 크로스-슬라이스 오염 방지: voucher_license 응답의 24-hex _id가 DDB 레코드와
            // 우연히 일치하는 경우를 source 필드로 명시적 구분.
            const isVoucherSource = action.license?.source === 'voucher_license';
            const licenseId = action.license?._id?.toString?.() ?? action.license?._id;
            const matchesMongo = (item) =>
                isVoucherSource && licenseId && (item._id?.toString?.() ?? item._id) === licenseId;
            const matchesDdb = (item) =>
                !isVoucherSource && item._id === action.license?._id && item.sk === action.license?.sk;

            // status → suspended 파생: voucher_license 응답에 suspended 필드가 없을 때
            // UI 표시(computeLicStatus)와 정합을 맞추기 위해 status에서 derive.
            const s = action.license?.status;
            const derivedSuspended =
                s === 'suspend' ? true :
                (s === 'active' || s === 'actived' || s === 'resume') ? false :
                undefined;

            const applyUpdate = (list) => list.map((item) => {
                const hit = matchesMongo(item) || matchesDdb(item);
                if (!hit) return item;
                return {
                    ...item,
                    // API status values (MongoDB SSoT): 'active' | 'actived' | 'suspend' | 'resume'
                    ...(s !== undefined ? { status: s } : {}),
                    memo: action.license.memo ?? item.memo,
                    suspended: derivedSuspended ?? action.license.suspended ?? item.suspended,
                    expired: action.license.expired ?? item.expired,
                    updated: action.license.updated ?? item.updated,
                };
            });
            return {
                ...state,
                customerLicenses: applyUpdate(state.customerLicenses),
                corpCustomerLicenses: applyUpdate(state.corpCustomerLicenses),
                dealerCustomerLicenses: applyUpdate(state.dealerCustomerLicenses),
            };
        }
        case SmartDentConstant.ISSUE_QR:
            return { ...state, issuedQrs: action.qrRecords };
        case SmartDentConstant.GET_CORP_CUSTOMER_LICENSES:
            return { ...state, corpCustomerLicenses: action.customerLicenses };
        case SmartDentConstant.GET_DEALER_CUSTOMER_LICENSES:
            return { ...state, dealerCustomerLicenses: action.customerLicenses };
        case SmartDentConstant.GET_ISSUED_QRS_BY_DEALER:
            return { ...state, issuedQrHistory: action.issuedQrs };
        case SmartDentConstant.GET_VOUCHER_TEMPLATE:
        case SmartDentConstant.SAVE_VOUCHER_TEMPLATE:
        case SmartDentConstant.RESET_VOUCHER_TEMPLATE:
            return { ...state, voucherTemplate: action.voucherTemplate };
        case SmartDentConstant.GET_GRANT_HISTORY:
            return { ...state, grantHistory: action.grantHistory };
        case SmartDentConstant.GET_DEALER_ALLOCATION_HISTORY:
            return { ...state, dealerAllocationHistory: action.dealerAllocationHistory };
        case SmartDentConstant.GET_ALLOCATION_NODES:
            return { ...state, allocationNodes: action.allocationNodes };
        case SmartDentConstant.GET_SCOPE_CONTEXT:
            return { ...state, scopeContext: action.scopeContext ?? null };
        case SmartDentConstant.APPEND_ALLOCATION_AUDIT: {
            // 새 audit 1건을 head 에 추가하고 작업 직후 UI 가 즉시 반영하도록 한다.
            // grant 는 grantHistory, allocate/revoke 는 dealerAllocationHistory 에 분기.
            const audit = action.audit;
            if (!audit) return state;
            if (audit.operation === 'grant') {
                return { ...state, grantHistory: [audit, ...state.grantHistory] };
            }
            if (audit.operation === 'allocate' || audit.operation === 'revoke') {
                return {
                    ...state,
                    dealerAllocationHistory: [audit, ...state.dealerAllocationHistory],
                };
            }
            return state;
        }
        case SmartDentConstant.UPDATE_DEALER_ALLOCATION: {
            const updated = action.allocation;
            if (!updated) return state;
            const idx = state.dealerAllocations.findIndex(
                (d) => d?._id === updated._id && d?.sk === updated.sk,
            );
            const dealerAllocations =
                idx >= 0
                    ? state.dealerAllocations.map((d, i) => (i === idx ? updated : d))
                    : [...state.dealerAllocations, updated];
            return {
                ...state,
                dealerAllocation: updated,
                dealerAllocations,
            };
        }
        default:
            return state;
    }
};

const isListedProductKey = (productKey) => LICENSE_PRODUCTS.some((p) => p.key === productKey);

// SMARTDent = 루트 객체, 그 외 제품 = byProduct[key]. 비대칭이라 SMARTDent 는 byProduct 에 없다.
const readSlice = (state, productKey) =>
    productKey === DEFAULT_LICENSE_PRODUCT.key
        ? state
        : state.byProduct?.[productKey] ?? EMPTY_LICENSE_PRODUCT_STATE;

const writeSlice = (state, productKey, next) => {
    if (productKey === DEFAULT_LICENSE_PRODUCT.key) return next;
    return { ...state, byProduct: { ...state.byProduct, [productKey]: next } };
};

export const SmartDentReducer = (state = initialState, action) => {
    if (action?.productKey != null && !isListedProductKey(action.productKey)) {
        console.warn('[guard] SmartDentReducer: unknown productKey', {
            handler: 'SmartDentReducer',
            target: { type: action.type, productKey: action.productKey },
            reason: 'productKey is not in LICENSE_PRODUCTS',
        });
        return state;
    }
    const productKey = action?.productKey ?? DEFAULT_LICENSE_PRODUCT.key;
    const slice = readSlice(state, productKey);
    const next = reduceProductSlice(slice, action ?? {});
    if (next === slice) return state;
    return writeSlice(state, productKey, next);
};

// 화면·action 의 유일한 읽기 경로. state 는 Redux 전체 state.
export const selectLicenseProductState = (state, productKey = DEFAULT_LICENSE_PRODUCT.key) => {
    const root = state?.SmartDentReducer;
    if (!root) return EMPTY_LICENSE_PRODUCT_STATE;
    if (!isListedProductKey(productKey)) {
        console.warn('[guard] selectLicenseProductState: unknown productKey', {
            handler: 'selectLicenseProductState',
            target: { productKey },
            reason: 'productKey is not in LICENSE_PRODUCTS',
        });
        return EMPTY_LICENSE_PRODUCT_STATE;
    }
    return readSlice(root, productKey);
};
